import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output } from '@angular/core'
import { FlexModule } from '@angular/flex-layout'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatTableModule } from '@angular/material/table'
import { PipesModule } from '@mmx/pipes'
import { TranslateModule } from '@ngx-translate/core'

@Component({
  selector: 'mmx-dropzone',
  styleUrls: ['./dropzone.component.scss'],
  templateUrl: './dropzone.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressBarModule,
    PipesModule,
    FlexModule,
    TranslateModule,
  ],
})
export class DropzoneComponent {
  @Input()
  public loading = false

  @Input()
  public allowedTypes: string[] = []

  @Output()
  public uploadFiles = new EventEmitter<File[]>()

  public dragover = false
  public files: File[] = []
  public selectedFiles: File[] = []

  public columns = ['select', 'name', 'size', 'type', 'actions']

  constructor(
    private cdr: ChangeDetectorRef,
  ) {}

  @HostListener('dragover', ['$event'])
  public onDragOver(evt: DragEvent){
    evt.preventDefault()
    evt.stopPropagation()

    if (this.loading) {
      return
    }

    this.dragover = true
  }

  @HostListener('dragleave', ['$event'])
  public onDragLeave(evt: DragEvent){
    evt.preventDefault()
    evt.stopPropagation()
    this.dragover = false
  }

  @HostListener('drop', ['$event'])
  public onDrop(evt: DragEvent){
    evt.preventDefault()
    evt.stopPropagation()

    if (this.loading) {
      return
    }

    this.dragover = false
    const files = evt.dataTransfer?.files
    if (files?.length) {
      const allowedFiles = Array.from(files).filter((file) => {
        if (this.allowedTypes.length) {
          return this.allowedTypes.includes(file.type)
        }

        return true
      })

      if (allowedFiles.length === 0) {
        return
      }

      this.files = [
        ...this.files,
        ...allowedFiles,
      ]
      this.toggleSelectAll( )
    }
  }

  public onDropzoneClick() {
    if (this.loading) {
      return
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    if (this.allowedTypes.length) {
      input.accept = this.allowedTypes.join(',')
    }

    input.onchange = () => {
      if (input.files?.length) {
        this.files = Array.from((input.files || []))
        this.toggleSelectAll()
        this.cdr.detectChanges()
      }
    }
    input.click()
  }

  public toggleSelectAll() {
    if (this.selectedFiles.length === this.files.length) {
      this.selectedFiles = []
    } else {
      this.selectedFiles = this.files
    }
  }

  public toggleSelect(file: File) {
    if (this.isSelected(file)) {
      this.selectedFiles = this.selectedFiles.filter(f => f !== file)
    } else {
      this.selectedFiles = [...this.selectedFiles, file]
    }
  }

  public isSelected(file: File) {
    return this.selectedFiles.includes(file)
  }

  public removeFile(file: File) {
    this.files = this.files.filter(f => f !== file)
    this.selectedFiles = this.selectedFiles.filter(f => f !== file)
  }

  public clear() {
    this.files = []
    this.selectedFiles = []
    this.cdr.detectChanges()
  }
}
